import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  NativePDFOptions,
  useNativePDFGenerator,
} from "@/hooks/useNativePDFGenerator";
import { usePDFGenerator } from "@/hooks/usePDFGenerator";
import { Order, OrderItem as ApiOrderItem, clientsApi, ordersApi } from "@/lib/api";
import { useUser } from "@/contexts/UserContext";
import { distributeCouponDiscount } from "@/lib/couponItems";
// Mapeamento do OrderItem da API para o formato esperado pelo PDF generator
interface PDFOrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  adjustment: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}

const USE_NATIVE_PDF = true;

// Helper para extrair brandId do código do produto
const extractBrandIdFromProductCode = (
  productCode: string | undefined | null,
): number => {
  if (!productCode) return 1;

  const code = String(productCode).toUpperCase();
  // Padrões conhecidos: CNXAZA, CNXAZP = AZ (1)
  if (code.includes("AZ")) return 1;
  if (code.includes("HF")) return 2; // High Five
  if (code.includes("ML")) return 3; // My Life
  if (code.includes("LEX")) return 4; // LEX
  if (code.includes("AI4")) return 5; // AI4SCHOOL
  return 1; // Default para AZ
};

// Helper para mapear UseId para utilizacao
const mapUseIdToUtilizacao = (useId?: number): string => {
  // 1 = Aluno, 2 = Professor
  if (useId === 2) return "Professor";
  return "Aluno"; // Default para Aluno (useId === 1 ou undefined)
};

export const useRegeneratePDF = () => {
  const { selectedClient } = useUser();
  const { toast } = useToast();
  const { generateNativePDF } = useNativePDFGenerator();
  const { generatePDF } = usePDFGenerator();

  // Geração única do PDF do pedido (2ª via, lista, detalhe e checkout): sempre a partir do pedido salvo na API.
  // `mode: "blob"` devolve o arquivo sem avisos nem download (usado ao finalizar o pedido, para o ZIP).
  const buildOrderPdf = useCallback(
    async (order: Order, mode: "download" | "blob" = "download", filenameOverride?: string): Promise<boolean | Blob> => {
      const notify = mode === "blob" ? () => undefined : toast;
      try {
        // Buscar pedido completo com todos os dados
        notify({
          title: "Carregando dados...",
          description: "Buscando informações completas do pedido",
        });

        // Fonte única: o pedido é sempre lido da API (a lista e o detalhe passam objetos com campos diferentes,
        // o que gerava PDFs diferentes para o mesmo pedido). Só se usa o objeto recebido se a leitura falhar.
        let freshOrder: Order | null = null;
        if (order.id) {
          try {
            freshOrder = await ordersApi.getById(order.id);
          } catch (error) {
            console.warn("Não foi possível reler o pedido; usando os dados recebidos.", error);
          }
        }

        let fullOrder: Order;
        if (freshOrder?.items && freshOrder.items.length > 0) {
          fullOrder = freshOrder;
        } else if (order.items && order.items.length > 0) {
          // Criar uma cópia profunda básica para evitar mutação do estado original
          const mappedItems = order.items.map((item: any) => ({
            id: item.id || crypto.randomUUID(),
            brand: item.Brand || item.brand || null,
            createdAt: order.createdAt,
            discountAmount: item.DiscountAmount ?? item.discountAmount,
            educationLevel: item.EducationLevel || item.educationLevel || null,
            itemTotal: item.ItemTotal ?? item.itemTotal,
            netPrice: item.NetPrice ?? item.netPrice,
            status: "active",
            productSeries: item.ProductSeries || item.productSeries,
            productSku: item.ProductSku || item.productSku,
            purchaseOrderId: order.id,
            quantityContracted:
              item.QuantityContracted ?? item.quantityContracted,
            quantityOrdered: item.QuantityOrdered ?? item.quantityOrdered,
            useId: item.useId ?? 0,
            contractualDiscount:
              item.ContractualDiscount ?? item.contractualDiscount,
            bonifiedQuantity: item.BonifiedQuantity ?? item.bonifiedQuantity,
            couponQuantity: item.CouponQuantity ?? item.couponQuantity,
            couponDiscountAmount: item.CouponDiscountAmount ?? item.couponDiscountAmount,
            adjustment: item.Adjustment ?? item.adjustment ?? 0,
            productCode: item.ProductCode || item.productCode || "",
            academicYear: item.AcademicYear || item.academicYear || null,
            productDescription:
              item.ProductDescription || item.productDescription,
            unitPrice: item.UnitPrice ?? item.unitPrice,
            brandId: item.brandId || item.BrandId,
            deliveryTypeId:
              item.deliveryTypeId ??
              item.DeliveryTypeId ??
              item.delivery_type_id ??
              item.delivery_type_number ??
              item.deliveryTypeNumber ??
              (order as any).deliveryTypeId ??
              (order as any).DeliveryTypeId ??
              (order as any).delivery_type_id ??
              (order.orderNumber?.includes("-S") || order.orderNumber?.includes("PED-S") ? 2 : 1),
          }));

          fullOrder = {
            ...order,
            items: mappedItems,
          };
        } else {
          fullOrder = await ordersApi.getById(order.id!);
        }

        // Tentar enriquecer dados do cliente com selectedClient se disponível e compatível
        // A verificação de String(fullOrder.clientCode) === String(selectedClient.codigo_cnx) garante que só enriquecemos
        // se for a mesma unidade, protegendo contra vazamento de dados de outras unidades.
        if (
          selectedClient?.codigo_cnx &&
          String(fullOrder.clientCode) === String(selectedClient.codigo_cnx)
        ) {
          fullOrder.customer = {
            ...(fullOrder.customer || {}),
            ...selectedClient,
          };
        } else if (fullOrder.clientCode) {
          // Sem o cliente selecionado (ex.: backoffice), o pedido não traz número/bairro/CEP: busca o cadastro do cliente
          // (rota existente) para o endereço sair completo, como no PDF gerado no momento do pedido.
          try {
            const registered: any = await clientsApi.getByCode(String(fullOrder.clientCode));
            if (registered) {
              fullOrder.customer = { ...registered, ...(fullOrder.customer || {}) } as any;
              const customer: any = fullOrder.customer;
              customer.clientNumber = customer.clientNumber || registered.clientNumber;
              customer.clientDistrict = customer.clientDistrict || registered.clientDistrict;
              customer.clientPostCode = customer.clientPostCode || registered.clientPostCode;
            }
          } catch (error) {
            console.warn("Não foi possível buscar o cadastro do cliente para o PDF.", error);
          }
        }

        if (!fullOrder.items || fullOrder.items.length === 0) {
          notify({
            title: "Erro",
            description: "Pedido não possui itens para gerar o PDF",
            variant: "destructive",
          });
          return false;
        }

        console.log("📦 Pedido completo recebido:", {
          hasCustomer: !!fullOrder.customer,
          customer: fullOrder.customer,
          clientName: fullOrder.clientName,
          clientCode: fullOrder.clientCode,
        });

        notify({
          title: "Gerando PDF...",
          description: "Recriando o PDF do pedido",
        });

        // Mapear os itens da API para o formato do PDF
        const pdfItems: PDFOrderItem[] = fullOrder.items.map((item) => {
          const desconto = parseFloat(
            `${item.contractualDiscount}`?.replace(",", ".") || "0",
          );
          const precoUnitario = item.unitPrice;
          const quantidade = item.quantityOrdered;

          // Extrair brandId do código do produto se não vier do backend
          const brandId =
            item.brandId || extractBrandIdFromProductCode(item.productCode);

          // Recalcular valores (o backend pode retornar 0 para itens com 100% de desconto)
          const valorLiquido = precoUnitario * (1 - desconto / 100);
          // Unidades bonificadas pelo Professor não são cobradas: o valor do item é o das unidades pagas, como no
          // PDF gerado ao finalizar o pedido.
          const bonified = Math.min(Math.max(Number((item as any).bonifiedQuantity ?? 0) || 0, 0), quantidade);
          const valorTotal = valorLiquido * (quantidade - bonified);

          const deliveryTypeId =
            (item as any).deliveryTypeId ??
            (item as any).DeliveryTypeId ??
            (item as any).delivery_type_id ??
            (item as any).delivery_type_number ??
            (fullOrder as any).deliveryTypeId ??
            (fullOrder as any).DeliveryTypeId ??
            (fullOrder as any).delivery_type_id ??
            (fullOrder.orderNumber?.includes("-S") || fullOrder.orderNumber?.includes("PED-S") ? 2 : 1);

          const mappedItem = {
            id: parseInt(item.id || "0"),
            codigo_produto: item.productCode,
            descricao_produto: item.productDescription,
            preco: precoUnitario,
            ano_letivo: item.academicYear || new Date().getFullYear(),
            marca: brandId.toString(), // ID da marca como string
            sku: item.productSku || "",
            status: "active",
            utilizacao: mapUseIdToUtilizacao(item.useId), // Mapeia UseId: 1=Aluno, 2=Professor
            serie: item.productSeries || null,
            segmento: item.educationLevel || null,
            quantidade_contratual: item.quantityContracted || 0,
            desconto: desconto,
            adjustment: item.adjustment || 0,
            quantidade_pedido: quantidade,
            valor_liquido: valorLiquido,
            valor_total: valorTotal,
            delivery_type_id: deliveryTypeId,
            delivery_type_number: deliveryTypeId,
            brand_id: brandId,
          };

          // Debug log para primeiros 3 itens
          if (parseInt(item.id || "0") < 3) {
            console.log("🔍 Item mapeado para PDF:", {
              produto: item.productDescription,
              serie: item.productSeries,
              useId: item.useId,
              utilizacao: mapUseIdToUtilizacao(item.useId),
              brandId: brandId,
              marca: brandId.toString(),
              preco: precoUnitario,
              desconto: desconto,
              quantidade: quantidade,
              valorLiquido: valorLiquido,
              valorTotal: valorTotal,
            });
          }

          return mappedItem;
        });

        // Calcular totais
        const total = pdfItems.reduce((sum, item) => sum + item.valor_total, 0);
        const grossSubtotal = pdfItems.reduce(
          (sum, item) => sum + item.preco * item.quantidade_pedido,
          0,
        );
        const netSubtotal = total;

        // Estatísticas dos itens
        const itensComQuantidade = pdfItems.filter(
          (i) => i.quantidade_pedido > 0,
        );
        const itensComValor = pdfItems.filter((i) => i.valor_total > 0);
        const itensComSerie = pdfItems.filter((i) => i.serie);

        console.log("📊 Totais calculados:", {
          totalItens: pdfItems.length,
          itensComQuantidade: itensComQuantidade.length,
          itensComValor: itensComValor.length,
          itensComSerie: itensComSerie.length,
          total: total,
          grossSubtotal: grossSubtotal,
          netSubtotal: netSubtotal,
        });

        console.log(
          "📋 Amostra de itens com valor > 0:",
          itensComValor.slice(0, 5).map((i) => ({
            descricao: i.descricao_produto,
            serie: i.serie,
            marca: i.marca,
            brandId: i.brand_id,
            quantidade: i.quantidade_pedido,
            valorLiquido: i.valor_liquido,
            valorTotal: i.valor_total,
          })),
        );

        console.log(
          "📋 TODOS os itens mapeados (resumo):",
          pdfItems.map((i, idx) => ({
            idx,
            descricao: i.descricao_produto.substring(0, 30),
            serie: i.serie,
            marca: i.marca,
            qtd: i.quantidade_pedido,
            vlrLiq: i.valor_liquido.toFixed(2),
            vlrTotal: i.valor_total.toFixed(2),
          })),
        );

        // Extrair informações do cliente (priorizar dados de customer, depois do pedido)
        const customer = fullOrder.customer;
        const clientInfo = {
          name:
            customer?.clientFantasyName ||
            customer?.nome_cliente ||
            fullOrder.clientName,
          code:
            customer?.clientCodeCnx ||
            customer?.codigo_cnx ||
            fullOrder.clientCode,
          cnpj:
            customer?.clientCnpj ||
            customer?.cnpj ||
            fullOrder.clientCnpj?.toString() ||
            "",
          address:
            customer?.clientAddress ||
            customer?.endereco ||
            fullOrder.clientAddress ||
            "",
          number:
            customer?.clientNumber ||
            customer?.numero ||
            fullOrder.clientNumber ||
            "",
          district:
            customer?.clientDistrict ||
            customer?.bairro ||
            fullOrder.clientDistrict ||
            "",
          city:
            customer?.clientCity ||
            customer?.cidade ||
            fullOrder.clientCity ||
            "",
          state:
            customer?.clientState ||
            customer?.estado ||
            fullOrder.clientState ||
            "",
          postCode:
            customer?.clientPostCode ||
            customer?.cep ||
            fullOrder.clientPostCode ||
            "",
        };

        const filename = filenameOverride ?? `Pedido_${fullOrder.orderNumber}_2aVia.pdf`;

        // Os itens do pedido guardam o valor cheio; o desconto do cupom está no pedido e é abatido do total.
        const couponDiscount = Number((fullOrder as any)?.couponDiscountAmount ?? (order as any)?.couponDiscountAmount ?? 0) || 0;

        const shippingAmount = (fullOrder as any)?.shippingAmount
          ? (fullOrder as any)?.shippingAmount
          : 0;

        const annualQtdFromOrder =
          typeof (fullOrder as any)?.annualQuantityInstallment === "number"
            ? (fullOrder as any)?.annualQuantityInstallment
            : (typeof (fullOrder as any)?.annual_quantity_installment === "number"
              ? (fullOrder as any)?.annual_quantity_installment
              : undefined);

        const semestralQtdFromOrder =
          typeof (fullOrder as any)?.semestralQuantityInstallment === "number"
            ? (fullOrder as any)?.semestralQuantityInstallment
            : (typeof (fullOrder as any)?.semestral_quantity_installment === "number"
              ? (fullOrder as any)?.semestral_quantity_installment
              : undefined);

        const rootQuantityInstallment =
          typeof fullOrder.quantityInstallment === "number"
            ? fullOrder.quantityInstallment
            : (typeof (fullOrder as any)?.quantity_installment === "number"
              ? (fullOrder as any)?.quantity_installment
              : undefined);

        const annualItem = pdfItems.find((i) => i.delivery_type_number === 1);
        const semestralItem = pdfItems.find((i) => i.delivery_type_number === 2);

        const annualQuantityInstallment =
          annualQtdFromOrder !== undefined
            ? annualQtdFromOrder
            : (rootQuantityInstallment !== undefined
              ? rootQuantityInstallment
              : (annualItem && typeof (annualItem as any).invoicingQtd === "number"
                ? (annualItem as any).invoicingQtd
                : undefined));

        const semestralQuantityInstallment =
          semestralQtdFromOrder !== undefined
            ? semestralQtdFromOrder
            : (rootQuantityInstallment !== undefined
              ? rootQuantityInstallment
              : (semestralItem && typeof (semestralItem as any).invoicingQtd === "number"
                ? (semestralItem as any).invoicingQtd
                : undefined));

        const pdfOptions: NativePDFOptions = {
          filename,
          shippingTotal: shippingAmount,
          orderNumber: fullOrder.orderNumber,
          clientName: clientInfo.name,
          clientCode: clientInfo.code,
          clientCNPJ: clientInfo.cnpj,
          clientAddress: clientInfo.address,
          clientNumber: clientInfo.number,
          clientDistrict: clientInfo.district,
          clientCity: clientInfo.city,
          clientState: clientInfo.state,
          clientPostCode: clientInfo.postCode,
          // Vêm de GET /orders/{id}, /orders/history e /orders/client/{code}; shippingAddress null em pedidos antigos.
          billingAddress: fullOrder.billingAddress ?? null,
          shippingAddress: fullOrder.shippingAddress ?? null,
          orderItems: pdfItems,
          grossSubtotal,
          createdBy: `${fullOrder.userEmail}`,
          userEmail: `${fullOrder.userEmail}`,
          quantityInstallment: rootQuantityInstallment,
          annualQuantityInstallment,
          semestralQuantityInstallment,
          netSubtotal,
          date: order?.createdAt || null,
          total: Math.max(0, total + shippingAmount - couponDiscount),
          couponDiscount,
          // Cupom por itens: aqui os itens são recalculados pelo valor cheio (acima), então o desconto segue
          // no total; o gerador aplica o desconto no valor do item (ou lista à parte) conforme a flag.
          couponCode: (fullOrder as any)?.couponCode ?? undefined,
          couponItems: fullOrder.items.some((item) => (item.couponQuantity ?? 0) > 0)
            ? fullOrder.items
              .filter((item) => (item.couponQuantity ?? 0) > 0)
              .map((item) => ({
                productCode: item.productCode,
                description: item.productDescription,
                quantity: item.couponQuantity ?? 0,
                discountAmount: item.couponDiscountAmount ?? 0,
              }))
            // Cupom do pedido todo (sem itens): o desconto é rateado nas linhas para todos os valores do PDF saírem líquidos.
            : (fullOrder as any)?.couponCode
              ? distributeCouponDiscount(
                pdfItems.map((item) => ({ productCode: item.codigo_produto, description: item.descricao_produto, total: item.valor_total })),
                couponDiscount,
              )
              : [],
          isScheduled: ( order as any )?.isScheduled || false,
        };

        console.log("🎯 Opções passadas para generateNativePDF:", {
          ...pdfOptions,
          orderItems: `${pdfOptions.orderItems.length} itens`,
          primeiroItem: pdfOptions.orderItems[0],
        });

        if (USE_NATIVE_PDF) {
          const pdfBlob = await generateNativePDF(pdfOptions);

          if (mode === "blob") return pdfBlob instanceof Blob ? pdfBlob : false;

          if (pdfBlob instanceof Blob) {
            // Create a URL for the blob
            const url = window.URL.createObjectURL(pdfBlob);
            // Create a temporary anchor element
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            // Programmatically click the anchor to trigger the download
            document.body.appendChild(a);
            a.click();
            // Clean up by removing the anchor and revoking the URL
            a.remove();
            window.URL.revokeObjectURL(url);

            notify({
              title: "PDF regenerado com sucesso!",
              description: `O download de ${filename} foi iniciado.`,
            });
          } else {
            // Error is already handled inside generateNativePDF, but we can add a fallback toast here if needed.
            notify({
              title: "Erro ao gerar PDF",
              description: "Não foi possível criar o arquivo PDF.",
              variant: "destructive",
            });
          }
        } else {
          // This fallback is to a different generator and should be handled if needed.
          // For now, we assume USE_NATIVE_PDF is the primary path.
          await generatePDF(filename);
          notify({
            title: "PDF regenerado com sucesso!",
            description: "Segunda via do pedido gerada (método legado).",
          });
        }

        return true;
      } catch (error) {
        console.error("Error regenerating PDF:", error);
        notify({
          title: "Erro ao regenerar PDF",
          description:
            error instanceof Error ? error.message : "Erro desconhecido",
          variant: "destructive",
        });
        return false;
      }
    },
    [toast, generateNativePDF, generatePDF, selectedClient],
  );

  const regeneratePDF = useCallback(
    async (order: Order) => (await buildOrderPdf(order, "download")) === true,
    [buildOrderPdf],
  );

  const generateOrderPdfBlob = useCallback(
    async (order: Order, filename?: string): Promise<Blob | false> => {
      const result = await buildOrderPdf(order, "blob", filename);
      return result instanceof Blob ? result : false;
    },
    [buildOrderPdf],
  );

  return { regeneratePDF, generateOrderPdfBlob };
};
